/**
 * PD-1282 — offer the portrait on an uploaded ID as the patient photo.
 *
 * Opens as soon as the ID upload succeeds: first a detecting state (the vision
 * model takes a few seconds to return a bounding box), then a pan/zoom crop
 * the user adjusts before anything is written. The model returns a box, never
 * an image — the crop is taken client-side from the original file. Nothing is
 * saved until the user has framed the crop and accepted it: a photo silently
 * derived from an approximate box on a government ID is the kind of error
 * nobody notices until it is wrong on a chart.
 */
import { useCallback, useEffect, useState } from 'react'
import Cropper, { type Area } from 'react-easy-crop'
import { Button, Group, Loader, Modal, Slider, Stack, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { IconZoomIn, IconZoomOut } from '@tabler/icons-react'
import { useClient } from '@/hooks/useClient'
import {
  cropToBlob,
  imageDimensions,
  toDownscaledBase64,
  type NormalizedBox,
} from '@/utils/cropImage'
import { logError } from '@/utils/errorUtils'

type Phase = 'detecting' | 'ready' | 'no-portrait' | 'unavailable'

type ImageSize = { width: number; height: number }

type Props = {
  patientId: string | undefined
  /** The just-uploaded ID image; null keeps the modal closed. */
  file: File | null
  onClose: () => void
  onSaved: () => unknown
}

/** ID-portrait aspect (width : height), like the photo on a driver's license. */
const PHOTO_ASPECT = 3 / 4

/** Smallest PHOTO_ASPECT rect covering the detected box, clamped to the image bounds. */
function portraitAroundBox(box: NormalizedBox, { width, height }: ImageSize): Area {
  const boxWidth = box.width * width
  const boxHeight = box.height * height
  let cropHeight = Math.max(boxHeight, boxWidth / PHOTO_ASPECT)
  let cropWidth = cropHeight * PHOTO_ASPECT
  const scale = Math.min(1, width / cropWidth, height / cropHeight)
  cropWidth *= scale
  cropHeight *= scale
  const centerX = (box.x + box.width / 2) * width
  const centerY = (box.y + box.height / 2) * height
  return {
    x: Math.min(Math.max(centerX - cropWidth / 2, 0), width - cropWidth),
    y: Math.min(Math.max(centerY - cropHeight / 2, 0), height - cropHeight),
    width: cropWidth,
    height: cropHeight,
  }
}

export function IdPhotoCropModal({ patientId, file, onClose, onSaved }: Props) {
  const client = useClient()
  const medplum = useMedplum()
  const [phase, setPhase] = useState<Phase>('detecting')
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageSize, setImageSize] = useState<ImageSize | null>(null)
  const [initialArea, setInitialArea] = useState<Area | undefined>(undefined)
  const [cropArea, setCropArea] = useState<Area | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!file || !patientId) return
    let active = true
    const url = URL.createObjectURL(file)
    setImageUrl(url)
    setPhase('detecting')
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCropArea(null)

    const detect = async () => {
      try {
        const imageBase64 = await toDownscaledBase64(file)
        const [size, region] = await Promise.all([
          imageDimensions(file),
          client.patientExtractIdPhoto({ id: patientId, imageBase64, mediaType: 'image/jpeg' }),
        ])
        if (!active) return
        if (!region.found || !region.box) {
          setPhase('no-portrait')
          return
        }
        const area = portraitAroundBox(region.box, size)
        setImageSize(size)
        setInitialArea(area)
        setCropArea(area)
        setPhase('ready')
      } catch (err) {
        // The ID upload already succeeded — a detection failure must not read
        // as an upload failure.
        logError('patient', 'Detect portrait on the identity document', err)
        if (active) setPhase('unavailable')
      }
    }
    void detect()

    return () => {
      active = false
      URL.revokeObjectURL(url)
    }
  }, [file, patientId, client])

  const savePhoto = useCallback(async () => {
    if (!patientId || !file || !cropArea || !imageSize) return
    setSaving(true)
    try {
      const blob = await cropToBlob(file, {
        x: cropArea.x / imageSize.width,
        y: cropArea.y / imageSize.height,
        width: cropArea.width / imageSize.width,
        height: cropArea.height / imageSize.height,
      })
      const attachment = await medplum.createAttachment({
        data: blob,
        contentType: 'image/jpeg',
        filename: 'patient-photo.jpg',
      })
      // Re-read rather than trusting a prop: the card's copy can be stale, and
      // this write must not clobber a concurrent demographics edit.
      const current = await medplum.readResource('Patient', patientId)
      await medplum.updateResource({ ...current, photo: [attachment] })
      onSaved()
      showNotification({ color: 'green', message: 'Patient photo updated from the ID.' })
      onClose()
    } catch (err) {
      logError('patient', 'Save patient photo from identity document', err)
      showNotification({
        color: 'red',
        title: 'Could not save the photo',
        message: 'The ID was saved. Please try setting the photo again.',
      })
    } finally {
      setSaving(false)
    }
  }, [patientId, file, cropArea, imageSize, medplum, onSaved, onClose])

  return (
    <Modal
      opened={Boolean(file)}
      onClose={onClose}
      centered
      size={460}
      radius="lg"
      overlayProps={{ backgroundOpacity: 0.45, blur: 3 }}
      title={
        <div>
          <Text fw={600}>Use this as the patient photo?</Text>
          <Text size="xs" c="dimmed">
            Detected on the uploaded ID — drag and zoom to adjust.
          </Text>
        </div>
      }
    >
      {phase === 'detecting' && (
        <Stack align="center" gap="sm" py={48}>
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Detecting the portrait on the ID…
          </Text>
        </Stack>
      )}

      {phase === 'ready' && imageUrl && (
        <Stack gap="md">
          <div className="relative h-[320px] overflow-hidden rounded-xl bg-slate-900">
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              maxZoom={4}
              aspect={PHOTO_ASPECT}
              showGrid={false}
              initialCroppedAreaPixels={initialArea}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_area, areaPixels) => setCropArea(areaPixels)}
            />
          </div>
          <Group gap="sm" wrap="nowrap">
            <IconZoomOut size={16} className="shrink-0 text-slate-400" />
            <Slider
              className="flex-1"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={setZoom}
              label={null}
              aria-label="Zoom"
            />
            <IconZoomIn size={16} className="shrink-0 text-slate-400" />
          </Group>
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" color="gray" disabled={saving} onClick={onClose}>
              Not now
            </Button>
            <Button loading={saving} onClick={() => void savePhoto()}>
              Use photo
            </Button>
          </Group>
        </Stack>
      )}

      {(phase === 'no-portrait' || phase === 'unavailable') && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {phase === 'no-portrait'
              ? "The ID was saved, but no portrait was detected — add the patient's photo manually."
              : "The ID was saved, but photo detection is unavailable right now. Add the patient's photo manually if needed."}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}
